import { Box, Typography, Button } from '@mui/material';
import { useBrandContext } from '../../../hooks/useBrandContext';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { getAdvisor, getCompanyLanding } from '../../../core/services/gutcheck-api.service';
import { useDealContext } from '../../../features/deal/context';
import Loading from '../../../components/Loading';
import { DocumentTitle } from '../../../components/DocumentTitle';
import { OnboardingSteps } from '../components/OnboardingSteps';

export const CompanyStart = () => {

    const { brandContext } = useBrandContext();
    const { dealContext, setDealContext } = useDealContext();
    const { companyTag: paramCompanyTag, advisorId } = useParams();
    const { brand } = brandContext;
    const companyTag = paramCompanyTag || brand?.tag;
    const [project, setProject] = useState<any>(null);
    const [surveyLink, setSurveyLink] = useState<any>(null);
    const [advisor, setAdvisor] = useState<any>(null);
    const [isFaqOpen, setIsFaqOpen] = useState<boolean>(false);
    const rootRef = useRef<HTMLElement | null>(null);

    const [searchParams] = useSearchParams();
    const scheduled = searchParams.get("scheduled");


    useEffect(() => {
        rootRef.current = document.getElementById('root');
    }, []);

    useEffect(() => {

        const fetchCompanyLanding = async () => {
            if (companyTag) {
                const { data, error } = await getCompanyLanding(companyTag);
                if (data) {
                    setProject(data.project);
                    setSurveyLink(data.surveyLink);

                    setDealContext({
                        ...dealContext,
                        project: data.project,
                        fetched: true
                    });
                }
            }
        }

        const fetchAdvisor = async () => {
            if (advisorId) {
                const { data, error } = await getAdvisor(advisorId);
                if (data) {
                    setAdvisor(data.advisor);
                }
            }
        }

        if (companyTag) {
            fetchCompanyLanding();
        }
        if (advisorId) {
            fetchAdvisor();
        }
    }, [companyTag, advisorId]);

    if (!advisor) {
        return <Loading />;
    }

    return (
        <>
            <DocumentTitle pageTitle={`${project?.organization?.name} Advisor Network for ${project?.name}`} showAttribution={false} />
            <Box sx={{
                width: '100%',
                background: 'linear-gradient(rgba(0,0,30,0.6), rgba(0,0,0,0.3)), url("/images/network.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '100vh',
                display: 'flex',
                height: '100%',
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: -1
            }}>
            </Box>

            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: "30px",
                maxWidth: '1200px',
                width: '100%',
                margin: '0 auto',
                height: 'fit-content',
                padding: "80px 80px",
                position: 'relative',
                zIndex: 1
            }}>
                <Box className="display-box white" sx={{ padding: "40px 40px", maxWidth: '700px', margin: '0 auto' }}>
                    <Typography variant="h5" fontSize={"1.7rem"} fontWeight="bold">
                        {(!project?.start_page_headline || project?.start_page_headline === '')
                            ? "20 shares have been allocated for your initial feedback"
                            : project?.start_page_headline}
                    </Typography>
                    <Typography variant="body1" mt={2}>
                        Helpful advisors will have opportunities to earn more equity, invest in the next round, and interact directly with the company and its investors.
                    </Typography>
                    {advisor.intro_enabled ? (
                        <Typography variant="h5" fontWeight="bold" fontSize={"1.2rem"} textAlign="center" mt={4} mb={4}>
                            Onboard as an advisor in 3 steps...
                        </Typography>
                    ) : (
                        <Typography variant="h5" fontWeight="bold" fontSize={"1.2rem"} textAlign="center" mt={4} mb={4}>
                            { scheduled ? "While you're waiting for your call..." : "" }
                        </Typography>
                    )}


                    <OnboardingSteps advisor={advisor} project={project} surveyLink={surveyLink} />


                    <Box display={isFaqOpen ? "none" : "flex"} flexDirection="row" gap="20px" mt={2} sx={{ justifyContent: 'space-between' }}>
                        <Button variant="text" color="primary" onClick={() => setIsFaqOpen(true)} sx={{ width: '100%' }}>Need more info?</Button>
                    </Box>




                    <Box display={isFaqOpen ? "flex" : "none"} flexDirection="row" gap="20px" mt={2} mb="20px" sx={{ justifyContent: 'space-between' }}>
                        <Box sx={{ flex: 1, backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "10px", border: "1px solid #c9c9c9", "& p": { fontSize: "0.9rem" } }}  >
                            <Typography variant="body1" fontWeight="bold" sx={{ borderBottom: "1px solid #c9c9c9", paddingBottom: "15px", marginBottom: "15px" }}>As an advisor, you'll...</Typography>
                            <p><strong>Provide monthly feedbck</strong> on new products and initiatives</p>
                            <p><strong>Refer colleagues</strong> who could also be good advisors or customers</p>
                            <p><strong>Schedule a demo</strong> or trial if and when the product fits your needs</p>
                        </Box>
                        <Box sx={{ flex: 1, backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "10px", border: "1px solid #c9c9c9", "& p": { fontSize: "0.9rem" } }}>
                            <Typography variant="body1" fontWeight="bold" sx={{ borderBottom: "1px solid #c9c9c9", paddingBottom: "15px", marginBottom: "15px" }}>Start now, finish later</Typography>
                            <p>Provide feedback in a mix of open-ended and multiple choice questions, many of which can be answered with a video response.</p>
                            <p>You can always pick back up at a later time.</p>
                        </Box>
                        <Box sx={{ flex: 1, backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "10px", border: "1px solid #c9c9c9", "& p": { fontSize: "0.9rem" } }}>
                            <Typography variant="body1" fontWeight="bold" sx={{ borderBottom: "1px solid #c9c9c9", paddingBottom: "15px", marginBottom: "15px" }}>Claim your shares</Typography>
                            <p>We've already allocated shares for your initial feedback with an opportunity to earn more.</p>
                            <p>If you're accepted as an advisor, we'll walk you through the process to claim your shares.</p>
                        </Box>
                    </Box>
                    <Box display={isFaqOpen ? "block" : "none"} sx={{ flex: 1, backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "10px", border: "1px solid #c9c9c9", "& p": { fontSize: "0.9rem" } }}  >
                        <Typography variant="body1" fontWeight="bold" sx={{ borderBottom: "1px solid #c9c9c9", paddingBottom: "15px", marginBottom: "15px" }}>About the Advisory Share Program</Typography>
                        <p>All advisors earn shares for each interaction the company marks as helpful, creating a direct link between contribution and value. Among them, a select group of lead advisors - designated by the company as especially impactful - have the opportunity to earn additional shares in recognition of their outsized influence.</p>
                        <p>Each advisor has access to a personalized dashboard where they can track the shares they've earned and see a detailed breakdown of the activities that generated them. The dashboard also highlights available opportunities to earn more, encouraging ongoing engagement and contribution.</p>
                        <p>Once or twice per year, advisors are invited to acquire the shares they've earned. These are held through a legal structure known as an Advisor Vehicle (AV), managed in partnership with AngelList. This structure ensures that advisors can participate in the upside of the companies they support - without the complexity of direct ownership or administrative overhead.</p>
                        <p>For more information, please see our <a href="https://info.brightloop.com/en/articles/11101661-advisory-share-program-overview" target="_blank" rel="noopener noreferrer">Advisory Share Program Overview</a>.</p>
                    </Box>
                    <Box display={isFaqOpen ? "flex" : "none"} mt={2} sx={{ justifyContent: 'center' }}>
                        <Typography fontSize={"0.8rem"}>
                            {(() => {
                                const emailAddress = `advisors@brightloop.com`;
                                return (
                                    <>
                                        Still have questions? Email us at <a href={`mailto:${emailAddress}`}>{emailAddress}</a>
                                    </>
                                );
                            })()}
                        </Typography>
                    </Box>


                </Box>

            </Box>

        </>
    );


}